import { useRef, Fragment, useEffect } from "react";
import { Offline, Online } from "react-detect-offline";

import { Outlet } from "react-router-dom";

import useUserQueryStore from "../context/UserStore";

import { appTitle, hasLoginExpired, loginStorageName, tokenStorageName, userStorageName } from "../utils/HelperUtils";

import { Box, CssBaseline, Stack, Typography } from "@mui/material";
import ADSAppBar from "../components/ADSAppBar";
import ADSNavContent from "../components/ADSNavContent";

import LoginDialog from "../dialogs/LoginDialog";

import WifiOffIcon from "@mui/icons-material/WifiOff";

import { appBarHeight } from "../utils/ADSStyles";
import { adsLightGreyD } from "../utils/ADSColour";

const Layout = () => {
  const showLogin = useUserQueryStore((s) => s.userQuery.showLogin);
  const currentUser = useUserQueryStore((s) => s.userQuery.currentUser);
  const setShowLogin = useUserQueryStore((s) => s.setShowLogin);
  const expiredToken = useUserQueryStore((s) => s.expiredToken);
  const setLoginDetails = useUserQueryStore((s) => s.setLoginDetails);
  const setCurrentUser = useUserQueryStore((s) => s.setCurrentUser);

  const onlinePolling = { enabled: true, url: "https://ipv4.icanhazip.com", interval: 5000, timeout: 5000 };
  const offlinePolling = { enabled: true, url: "https://ipv4.icanhazip.com", interval: 5000, timeout: 5000 };

  const loginMessage = useRef("Enter your credentials");

  // #region Login functions

  /**
   * Method to handle the login dialog close event.
   */
  const handleLoginClose = () => {
    setShowLogin(false);
  };

  // #endregion Login functions

  useEffect(() => {
    if (localStorage.getItem(loginStorageName) !== null) {
      const storedLogin: string | null = localStorage.getItem(loginStorageName);
      if (storedLogin) {
        const savedLogin = JSON.parse(storedLogin);
        if (savedLogin.expiry && hasLoginExpired(savedLogin.expiry)) {
          loginMessage.current = "Your session has expired. Please log in again.";
          expiredToken();
          localStorage.removeItem(loginStorageName);
          localStorage.removeItem(tokenStorageName);
          localStorage.removeItem(userStorageName);
        } else {
          setLoginDetails(savedLogin);

          if (localStorage.getItem(userStorageName) !== null && !currentUser) {
            const storedUser: string | null = localStorage.getItem(userStorageName);
            if (storedUser) {
              const savedUser = JSON.parse(storedUser);
              setCurrentUser(savedUser);
            }
          }
        }
      }
    } else {
      loginMessage.current = "Enter your credentials";
      expiredToken();
    }
  }, [expiredToken, setCurrentUser, setLoginDetails, currentUser, setShowLogin]);

  return currentUser ? (
    <>
      <Online polling={onlinePolling}>
        <Fragment>
          <div style={{ display: "flex" }}>
            <CssBaseline />
            <ADSAppBar />
            <ADSNavContent />
            <main style={{ flexGrow: 1, paddingLeft: "10px", paddingRight: "10px" }}>
              <div style={{ height: `${appBarHeight}px` }} />
              <Outlet />
            </main>
          </div>
        </Fragment>
        <LoginDialog isOpen={showLogin} title={appTitle} message={loginMessage.current} onClose={handleLoginClose} />
      </Online>
      <Offline polling={offlinePolling}>
        <Box
          sx={{
            paddingTop: "30px",
            textAlign: "center",
            backgroundColor: adsLightGreyD,
            height: "100vh",
          }}
        >
          <Stack direction="column" spacing={2} justifyContent="center" alignItems="center" sx={{ height: "100vh" }}>
            <WifiOffIcon sx={{ height: "100px", width: "100px" }} />
            <Typography variant="h1" sx={{ marginBottom: "5px" }}>
              Couldn't connect
            </Typography>
            <Typography variant="h4" sx={{ margin: "0" }}>
              Check your internet connection and try again.
            </Typography>
          </Stack>
        </Box>
      </Offline>
    </>
  ) : (
    <LoginDialog isOpen={showLogin} title={appTitle} message={loginMessage.current} onClose={handleLoginClose} />
  );
};

export default Layout;
